/**
 * useHomeData — fetches GET /api/v1/dashboard/home and exposes a partial
 * payload that the HomePage merges over its existing mock constants.
 *
 * Live-data fields (when backend is reachable):
 *   • hero.revenueToday
 *   • hero.fansJoinedToday
 *   • hero.creatorsJoinedToday
 *   • trend.revenueLast12Months  (12-mo monthly area chart)
 *   • leaderboards.topCreators   (top 5)
 *   • leaderboards.topFans       (top 5, masked handles)
 *
 * Mock-fallback fields (frontend keeps using its own constants):
 *   • hero.brandsJoinedToday   (no brand-summary query yet)
 *   • LiveStrip
 *   • CALL_VOLUME_BAR (calls by day-of-week)
 *   • REVENUE_MIX (today's mix by source)
 *   • the 4 sub-section tile groups
 */
import { useEffect, useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const FETCH_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`timeout after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export function useHomeData(range = null, rangeId = null, refreshKey = 0) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  const rangeKey = range ? `${range.from || ''}|${range.to || ''}|${rangeId || ''}` : '';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const controller = new AbortController();
    try {
      const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
      const apiKey = process.env.REACT_APP_API_KEY;
      const headers = {};
      if (token)       headers.Authorization = `Bearer ${token}`;
      else if (apiKey) headers["X-API-Key"]  = apiKey;
      const params = new URLSearchParams();
      if (range && range.from) params.set('from', range.from);
      if (range && range.to)   params.set('to',   range.to);
      // Pass the pill identity (today / this-week / this-month / this-year /
      // last-month / last-year / till-date / custom) so the backend can pick
      // the correct previous-period window — full last month for this-month,
      // full last year for this-year, etc. Without this the backend tries to
      // guess from window-length and gets it wrong on partial periods (e.g.
      // "This month" on the 1st of the month is a 1-day window).
      if (rangeId) params.set('rangeId', rangeId);
      const qs = params.toString();
      const res = await withTimeout(
        fetch(`${API_BASE}/dashboard/home${qs ? `?${qs}` : ''}`, {
          headers,
          signal: controller.signal,
        }),
        FETCH_TIMEOUT_MS,
        controller,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
      setData({ ...json.data, _isMock: false });
    } catch (e) {
      setError(e.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey, rangeKey]);

  return { data, error, loading, reload: load };
}

// ─── Formatters used by HomePage to render live data into the existing tile shape ───
export function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
export function formatNum(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-IN");
}
export function formatDelta(d) {
  if (!d || d.value == null) return null;
  return { direction: d.direction || "flat", value: d.value.toFixed(1) + "%" };
}
